// src/billing/CreateProposal.js
// LABOR PROPOSAL builder — the GC-facing, labor-only counterpart to a quote.
// Born on a BID (pre-PO, the normal case); when the bid is Won, the backend
// back-fills the project link automatically. Captures units × sqft so $/sqft
// analytics accumulate from real pricing history. PS{yymmdd}-{seq} numbering.
import { API_BASE } from '../apiBase';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { Plus, X, Trash2, ChevronUp, ChevronDown } from 'lucide-react';

const authH = () => ({ headers: { Authorization: localStorage.getItem('token') } });

// ---- boilerplate defaults, distilled from the Patel proposal ----
const DEFAULT_BASIS = 'Non-prevailing wage. Labor and tools by Stalwart Electric. All material and consumables by GC. Rental equipment by GC, or by Stalwart Electric at additional cost.';
const DEFAULT_FOOTNOTE = 'Labor only. Prices above are a flat labor fee and include no material, consumables, equipment rental, permit or plan-check fees, city fees, utility fees, or sales tax. All of these are by the GC or billed separately.';
const DEFAULT_PREAMBLE = 'All work will conform to the current California Electrical Code, the California Energy Code (Title 24), and local amendments, as called out on the plans.';
const DEFAULT_EXCLUSIONS = [
  'All material and consumables — panels, breakers, conduit, wire, cable, boxes, devices, fixtures, and all consumables by GC. Material takeoff list available on request.',
  'Rental equipment — lifts, scaffolding and similar by GC, or by Stalwart Electric at additional cost.',
  'Permits and fees — building permit, plan check, utility application, engineering, service and connection fees, utility deposits. Permits and utility application by GC.',
  'Utility work — service drop, pole work, meters, utility-side changes. Stalwart Electric coordinates on site only.',
  'Low voltage — data, phone, CATV, doorbells, intercom, security and cameras; available at additional cost on request.',
  'Site work — trenching, underground conduit, site, pathway or landscape lighting are not in the base bid; available at additional cost on request.',
  'Temporary power — construction power pole or temp panel.',
  'Patching and painting — drywall and stucco patching, paint and finish repair.',
  'Appliances and equipment — furnished and set by others. Stalwart Electric makes final connections once set.',
  'Return trips — rework, damage by others, or work not ready per schedule billed at $125/hour with a 4-hour minimum per trip.',
];
const DEFAULT_TERMS = [
  { label: 'Proposal valid', text: '30 days from the issue date. After that, pricing is subject to review before acceptance.' },
  { label: 'Start of work', text: 'Pricing assumes work begins within 90 days of acceptance. If the project has not started within 90 days, Stalwart Electric may re-price labor to current rates before mobilizing, and the schedule will be re-confirmed.' },
  { label: 'Scheduling', text: 'Pricing assumes continuous mobilizations with clear access and a dry, lockable site. Stalwart Electric shall be notified immediately of any schedule change that affects electrical work. Electrical rough-in will not be covered until it has passed inspection and Stalwart Electric has been notified.' },
  { label: 'Jobsite security', text: "Material storage, job boxes, containers and site security by GC. Stalwart Electric is not responsible for theft, loss or damage to material or equipment on site. Re-installation of stolen or damaged work will be billed as a change order." },
  { label: 'Payment', text: 'Weekly. Payment is due within 7 days of the invoice. No retention is held.' },
  { label: 'Change orders', text: 'Written and approved before work proceeds. Labor at $125/hour. Return trips billed at a 4-hour minimum per trip.' },
  { label: 'Insurance', text: "General liability and workers' compensation certificates provided on request." },
  { label: 'Warranty', text: 'One year on workmanship from final inspection. Material warranty by manufacturer/GC.' },
  { label: 'License', text: 'CSLB #1109509, C-10 Electrical.' },
];
const STATUSES = ['Draft', 'Sent', 'Accepted', 'Rejected', 'Revised'];
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function CreateProposal() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('id');
  const bidParam = searchParams.get('bid');

  const [currentId, setCurrentId] = useState(editId || null);
  const [savedNumber, setSavedNumber] = useState(null);
  const [status, setStatus] = useState('Draft');
  const [bidRef, setBidRef] = useState(bidParam || null);
  const [bidTitle, setBidTitle] = useState('');
  const [bidStage, setBidStage] = useState('');

  const [f, setF] = useState({
    preparedFor: '', projectName: '', plansRef: '', description: '', basis: DEFAULT_BASIS,
    units: '', sqftPerUnit: '', totalSqft: '',
    rfi: '', paymentNote: '', laborFootnote: DEFAULT_FOOTNOTE,
  });
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));
  const [lineItems, setLineItems] = useState([{ description: '', qty: 1, unitPrice: 0 }]);
  const [scopeSections, setScopeSections] = useState([{ title: 'SCOPE — ITEM 1', preamble: DEFAULT_PREAMBLE, bulletsText: '' }]);
  const [exclusions, setExclusions] = useState(DEFAULT_EXCLUSIONS.join('\n'));
  const [terms, setTerms] = useState(DEFAULT_TERMS);
  const [toast, setToast] = useState('');
  const [sendStage, setSendStage] = useState('');

  // (PDF header logo + Roboto fonts come from proposalAssets, extracted from
  // the master template — see buildPDF.)

  // ---- Exhibits: attachments bundled into the downloaded PDF ----
  // Each file becomes EXHIBIT 1, 2, 3… (array order IS the exhibit number);
  // arrows renumber, X removes. PDFs keep their vector quality; everything
  // lands on 8.5×11 portrait pages with an EXHIBIT n — PAGE x OF y stamp.
  const [exhibits, setExhibits] = useState([]);
  const [exDragging, setExDragging] = useState(false);
  const [exBusy, setExBusy] = useState('');
  const EX_OK = /\.(pdf|jpe?g|png)$/i;
  const addExhibits = async (list) => {
    const files = Array.from(list || []).filter(Boolean);
    if (!files.length) return;
    const bad = files.filter(x => !EX_OK.test(x.name));
    if (bad.length) alert(`Not supported as exhibits (use PDF, JPG or PNG — export Word docs to PDF first):\n\n${bad.map(b => b.name).join('\n')}`);
    const good = files.filter(x => EX_OK.test(x.name));
    for (let i = 0; i < good.length; i++) {
      setExBusy(`${i + 1} of ${good.length}`);
      try {
        // page count now, in the browser — the printed exhibit list needs it
        let pages = 1;
        if (/\.pdf$/i.test(good[i].name)) {
          try {
            const { PDFDocument } = await import('pdf-lib');
            pages = (await PDFDocument.load(await good[i].arrayBuffer(), { ignoreEncryption: true })).getPageCount();
          } catch (_) { pages = 0; }
        }
        const fd = new FormData(); fd.append('file', good[i]); fd.append('number', proposalNumber);
        const r = await axios.post(`${API_BASE}/proposals/exhibits/upload`, fd, authH());
        setExhibits(prev => [...prev, { ...r.data, title: '', pages }]);
      } catch (e) { alert(`"${good[i].name}" failed to upload: ` + (e.response?.data?.error || e.message)); }
    }
    setExBusy('');
  };
  // ---- Prepared-for picker: search Customers + Relationships, or add new ----
  const [pfOpen, setPfOpen] = useState(false);
  const [pfSel, setPfSel] = useState(null); // picked option {customerId?/relationshipId?, plain, phone, email, isGC}
  const [pfPool, setPfPool] = useState([]);
  useEffect(() => {
    Promise.all([
      axios.get(`${API_BASE}/customers`, authH()).then(r => r.data || []).catch(() => []),
      axios.get(`${API_BASE}/relationships`, authH()).then(r => r.data || []).catch(() => []),
    ]).then(([cs, rs]) => {
      setPfPool([
        ...cs.map(c => ({ kind: 'customer', customerId: c._id, name: c.name, plain: c.name, sub: c.businessName || c.type || '', phone: c.primaryPhone || '', email: c.primaryEmail || '', isGC: c.type === 'Contractor' || c.type === 'Builder' })),
        ...rs.map(x => ({ kind: 'company', relationshipId: x._id, name: x.contactName ? `${x.contactName} — ${x.company}` : x.company, plain: x.contactName || x.company, sub: x.company, phone: x.phone || '', email: x.email || '', isGC: (x.type || 'Contractor') === 'Contractor' })),
      ]);
    });
  }, []);
  const pfQuery = f.preparedFor.replace(/\s*\(General Contractor\)\s*$/i, '').trim().toLowerCase();
  const pfMatches = pfOpen && pfQuery.length >= 2 ? pfPool.filter(o => (o.name + ' ' + (o.sub || '')).toLowerCase().includes(pfQuery)).slice(0, 8) : [];
  const pickPf = (o) => {
    setF(prev => ({ ...prev, preparedFor: o.isGC ? `${o.plain} (General Contractor)` : o.plain }));
    setPfSel(o); setPfOpen(false);
  };
  const addPfCustomer = async () => {
    const name = f.preparedFor.replace(/\s*\(General Contractor\)\s*$/i, '').trim();
    if (!name) return;
    try {
      const r = await axios.post(`${API_BASE}/customers`, { name, type: 'Contractor', status: 'Lead' }, authH());
      const opt = { kind: 'customer', customerId: r.data._id, name, plain: name, sub: 'Contractor', phone: '', email: '', isGC: true };
      setPfPool(prev => [opt, ...prev]);
      pickPf(opt);
      setToastMsg(`"${name}" added to Customers.`);
    } catch (e) { alert('Could not add customer: ' + (e.response?.data?.error || e.message)); }
  };

  const moveExhibit = (i, dir) => setExhibits(prev => {
    const j = i + dir; if (j < 0 || j >= prev.length) return prev;
    const next = prev.slice(); [next[i], next[j]] = [next[j], next[i]]; return next;
  });
  const removeExhibit = (i) => {
    const ex = exhibits[i];
    if (!window.confirm(`Remove Exhibit ${i + 1} ("${ex.name}")? The file is deleted from storage.`)) return;
    setExhibits(prev => prev.filter((_, j) => j !== i));
    axios.delete(`${API_BASE}/proposals/exhibits/file`, { data: { path: ex.path }, headers: { Authorization: localStorage.getItem('token') } }).catch(() => {});
  };

  // ---- Draft persistence (new proposals) ----
  // Navigating to another component unmounts this page; without this, a
  // half-filled proposal was gone. Every change autosaves to localStorage
  // (keyed per bid) and restores when you come back; a successful Save
  // clears the draft so stale copies never resurface.
  const draftKey = editId ? null : `proposal.draft.${bidParam || 'new'}`;
  const draftReady = useRef(false);
  const [draftOff, setDraftOff] = useState(false);
  useEffect(() => {
    if (!draftKey) return;
    try {
      const raw = localStorage.getItem(draftKey);
      if (raw) {
        const d = JSON.parse(raw);
        if (d.f) setF(prev => ({ ...prev, ...d.f }));
        if (Array.isArray(d.lineItems) && d.lineItems.length) setLineItems(d.lineItems);
        if (Array.isArray(d.scopeSections) && d.scopeSections.length) setScopeSections(d.scopeSections);
        if (typeof d.exclusions === 'string') setExclusions(d.exclusions);
        if (Array.isArray(d.terms) && d.terms.length) setTerms(d.terms);
        if (Array.isArray(d.exhibits) && d.exhibits.length) setExhibits(d.exhibits);
        setToastMsg('Unsaved draft restored.');
      }
    } catch (_) {}
    draftReady.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!draftKey || !draftReady.current || draftOff) return;
    const t = setTimeout(() => {
      try { localStorage.setItem(draftKey, JSON.stringify({ f, lineItems, scopeSections, exclusions, terms, exhibits })); } catch (_) {}
    }, 400);
    return () => clearTimeout(t);
  }, [draftKey, draftOff, f, lineItems, scopeSections, exclusions, terms, exhibits]);
  const clearDraft = () => {
    if (!draftKey) return;
    setDraftOff(true);
    try { localStorage.removeItem(draftKey); } catch (_) {}
  };

  // PS{yymmdd}-{seq} — date-based, no PO needed
  const [existing, setExisting] = useState([]);
  useEffect(() => {
    axios.get(`${API_BASE}/proposals`, authH()).then(r => setExisting(r.data || [])).catch(() => {});
  }, []);
  const proposalNumber = useMemo(() => {
    if (savedNumber) return savedNumber;
    const d = new Date();
    const stamp = `${String(d.getFullYear()).slice(-2)}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
    const todays = existing.filter(p => (p.number || '').startsWith(`PS${stamp}`)).length;
    return `PS${stamp}-${String(todays + 1).padStart(2, '0')}`;
  }, [existing, savedNumber]);

  // Arriving with ?bid= — prefill from the bid
  useEffect(() => {
    if (!bidParam || editId) return;
    axios.get(`${API_BASE}/bids/${bidParam}`, authH()).then(({ data: b }) => {
      setBidTitle(b.title || '');
      setBidStage(b.stage || '');
      const addr = [b.jobSiteAddress?.street, b.jobSiteAddress?.city, b.jobSiteAddress?.state, b.jobSiteAddress?.zip].filter(Boolean).join(', ');
      setF(prev => ({
        ...prev,
        preparedFor: prev.preparedFor || [b.customer?.name || b.contactName, '(General Contractor)'].filter(Boolean).join(' '),
        projectName: prev.projectName || [b.title, addr].filter(Boolean).join(' — '),
      }));
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // EDIT mode — load the saved proposal
  useEffect(() => {
    if (!editId) return;
    axios.get(`${API_BASE}/proposals/${editId}`, authH()).then(({ data: p }) => {
      setCurrentId(p._id); setSavedNumber(p.number); setStatus(p.status || 'Draft');
      setBidRef(p.bid?._id || p.bid || null); setBidTitle(p.bid?.title || '');
      setF({
        preparedFor: p.preparedFor || '', projectName: p.projectName || '', plansRef: p.plansRef || '',
        description: p.description || '', basis: p.basis || DEFAULT_BASIS,
        units: p.units ?? '', sqftPerUnit: p.sqftPerUnit ?? '', totalSqft: p.totalSqft ?? '',
        rfi: p.rfi || '', paymentNote: p.paymentNote || '', laborFootnote: p.laborFootnote || DEFAULT_FOOTNOTE,
      });
      setLineItems((p.lineItems && p.lineItems.length) ? p.lineItems.map(li => ({ description: li.description, qty: li.qty, unitPrice: li.unitPrice })) : [{ description: '', qty: 1, unitPrice: 0 }]);
      setScopeSections((p.scopeSections && p.scopeSections.length)
        ? p.scopeSections.map(sc => ({ title: sc.title, preamble: sc.preamble, bulletsText: (sc.bullets || []).join('\n') }))
        : [{ title: 'SCOPE — ITEM 1', preamble: DEFAULT_PREAMBLE, bulletsText: '' }]);
      setExclusions((p.exclusions && p.exclusions.length) ? p.exclusions.join('\n') : DEFAULT_EXCLUSIONS.join('\n'));
      setTerms((p.terms && p.terms.length) ? p.terms : DEFAULT_TERMS);
      setExhibits(p.exhibits || []);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId]);

  const total = lineItems.reduce((s, li) => s + (Number(li.qty) || 0) * (Number(li.unitPrice) || 0), 0);
  const effSqft = Number(f.totalSqft) || (Number(f.units) || 0) * (Number(f.sqftPerUnit) || 0);
  const perSqft = effSqft > 0 ? total / effSqft : null;

  const buildPayload = () => ({
    number: proposalNumber,
    bid: bidRef || null,
    preparedFor: f.preparedFor, projectName: f.projectName, plansRef: f.plansRef,
    description: f.description, basis: f.basis,
    lineItems: lineItems.filter(li => li.description.trim()).map(li => ({
      description: li.description, qty: Number(li.qty) || 0, unitPrice: Number(li.unitPrice) || 0,
      amount: (Number(li.qty) || 0) * (Number(li.unitPrice) || 0),
    })),
    totalLabor: total,
    laborFootnote: f.laborFootnote,
    units: f.units === '' ? null : Number(f.units),
    sqftPerUnit: f.sqftPerUnit === '' ? null : Number(f.sqftPerUnit),
    totalSqft: effSqft || null,
    scopeSections: scopeSections.filter(sc => sc.title.trim() || sc.bulletsText.trim()).map(sc => ({
      title: sc.title, preamble: sc.preamble,
      bullets: sc.bulletsText.split('\n').map(x => x.trim()).filter(Boolean),
    })),
    rfi: f.rfi,
    exclusions: exclusions.split('\n').map(x => x.trim()).filter(Boolean),
    terms: terms.filter(t => t.label.trim() || t.text.trim()),
    exhibits,
    paymentNote: f.paymentNote,
    status,
    expirationDate: new Date(Date.now() + 30 * 24 * 3600 * 1000),
  });

  const save = async () => {
    if (!f.projectName.trim()) { alert('Give the proposal a project name.'); return null; }
    try {
      // A proposal is proof you're bidding. No linked bid yet → open one in
      // Bidding automatically so the paper lives in the pipeline (Won will
      // promote it, Lost will reject it, like any other bid paper). Linked
      // to a bid still sitting in Lead → nudge it to Bidding.
      let bidId = bidRef, createdBid = null;
      if (!bidId) {
        try {
          const nb = await axios.post(`${API_BASE}/bids`, {
            title: f.projectName.trim(),
            customer: pfSel?.customerId || null,
            contactName: pfSel?.customerId ? '' : (pfSel?.plain || f.preparedFor.replace(/\s*\(General Contractor\)\s*$/i, '').trim() || 'See proposal'),
            contactPhone: pfSel?.phone || '', contactEmail: pfSel?.email || '',
            stage: 'Bidding', source: 'Proposal', value: total,
          }, authH());
          bidId = nb.data._id; createdBid = nb.data.title;
          setBidRef(bidId); setBidTitle(nb.data.title); setBidStage('Bidding');
        } catch (_) { /* bid creation is best-effort — the proposal still saves */ }
      } else if (bidStage === 'Lead') {
        axios.patch(`${API_BASE}/bids/${bidId}`, { stage: 'Bidding' }, authH()).then(() => setBidStage('Bidding')).catch(() => {});
      }
      const payload = { ...buildPayload(), bid: bidId || null };
      if (pfSel?.relationshipId) payload.relationship = pfSel.relationshipId;
      if (currentId) {
        const r = await axios.put(`${API_BASE}/proposals/${currentId}`, payload, authH());
        clearDraft();
        setToastMsg(createdBid ? `Updated — created bid "${createdBid}" in Bidding.` : 'Proposal updated.');
        return r.data;
      }
      const r = await axios.post(`${API_BASE}/proposals`, payload, authH());
      setCurrentId(r.data._id); setSavedNumber(r.data.number);
      clearDraft();
      setToastMsg(createdBid ? `Saved — created bid "${createdBid}" in Bidding.` : 'Proposal saved as Draft.');
      return r.data;
    } catch (e) {
      alert('Save failed: ' + (e.response?.data?.error || e.message));
      return null;
    }
  };
  const setToastMsg = (m) => { setToast(m); setTimeout(() => setToast(''), 3000); };

  // ---- PDF ----
  // Matches the master template (stalwart_proposal_template.html — the
  // approved 6th St proposal PS261005-01) exactly: embedded Roboto + horse
  // logo, light-gray pricing table with thin rules, two-column scope and
  // exclusions with bold lead phrases, red-barred RFI box, long-form dates,
  // bold signature labels, PAGE X OF Y footer. All 8.5 × 11 letter.
  const buildPDF = async (skipFooter = false) => {
    const A = await import('./proposalAssets');
    const doc = new jsPDF({ unit: 'mm', format: 'letter' });
    doc.addFileToVFS('Roboto.ttf', A.ROBOTO_REGULAR);
    doc.addFileToVFS('Roboto-Bold.ttf', A.ROBOTO_BOLD);
    doc.addFileToVFS('Roboto-Light.ttf', A.ROBOTO_LIGHT);
    doc.addFont('Roboto.ttf', 'Roboto', 'normal');
    doc.addFont('Roboto-Bold.ttf', 'Roboto', 'bold');
    doc.addFont('Roboto-Light.ttf', 'RobotoLight', 'normal');
    const R = (bold) => doc.setFont('Roboto', bold ? 'bold' : 'normal');
    const RL = () => doc.setFont('RobotoLight', 'normal');

    // master geometry: letter, 0.55in side margins
    const W = 215.9, M = 14, BOT = 263;
    const RED = [242, 63, 79], INK = [17, 17, 17], SUB = [68, 68, 68], RULE = [153, 153, 153];
    const FS = 9.2, LH = 4.2;
    let y = 14;
    const nl = (need = 8) => { if (y + need > BOT) { doc.addPage(); y = 14; } };
    const longDate = (d) => d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

    // Mixed bold/normal word-wrapped text (bold leads, footnotes, RFI).
    // Honors explicit \n as line breaks; wrapped lines can hang-indent;
    // the fit check ignores the trailing space so lines fill properly.
    const richWalk = (segs, x, y0, maxW, lh, size, indent, drawIt) => {
      let cx = x, cy = y0;
      segs.forEach(sg => {
        R(sg.bold); doc.setFontSize(sg.size || size);
        if (drawIt) { const c = sg.color || INK; doc.setTextColor(c[0], c[1], c[2]); }
        String(sg.t || '').split('\n').forEach((line, li) => {
          if (li > 0) { cx = x + indent; cy += lh; }
          line.split(/[ \t]+/).filter(Boolean).forEach(wd => {
            const wordW = doc.getTextWidth(wd);
            if (cx + wordW > x + maxW && cx > x + indent) { cx = x + indent; cy += lh; }
            if (drawIt) doc.text(wd, cx, cy);
            cx += wordW + doc.getTextWidth(' ');
          });
        });
      });
      return cy + lh;
    };
    const richH = (segs, maxW, lh = LH, size = FS, indent = 0) =>
      richWalk(segs, 0, 0, maxW, lh, size, indent, false);
    const richDraw = (segs, x, y0, maxW, lh = LH, size = FS, indent = 0) =>
      richWalk(segs, x, y0, maxW, lh, size, indent, true);
    const leadSplit = (s) => {
      const m = /\s[—–-]\s/.exec(s);
      return m ? { lead: s.slice(0, m.index), rest: s.slice(m.index) } : { lead: null, rest: s };
    };
    // two-column flow (scope bullets, exclusions) — like the master.
    // headerFn (section title + any preamble) prints INSIDE the same
    // page-break decision, so a heading can never strand at a page bottom
    // with its list on the next page.
    const twoCol = (items, measure, draw, headerFn, headerH = 0) => {
      const gap = 5.6, colW = (W - 2 * M - gap) / 2;
      const hs = items.map(it => measure(it, colW));
      const totalH = hs.reduce((a, b) => a + b, 0);
      if (totalH / 2 > BOT - 20) { // too tall even split — one column
        if (headerFn) headerFn();
        items.forEach(it => { const h = measure(it, W - 2 * M); nl(h + 1); y = draw(it, M, y, W - 2 * M); });
        return;
      }
      // best split = the one whose taller column is shortest (balanced, like
      // CSS column-count in the master)
      let split = 1, blockH = Infinity;
      for (let s = 1; s <= items.length; s++) {
        const l = hs.slice(0, s).reduce((a, b) => a + b, 0);
        const r = hs.slice(s).reduce((a, b) => a + b, 0);
        const h = Math.max(l, r);
        if (h < blockH - 0.01) { blockH = h; split = s; }
      }
      nl(blockH + headerH + 2);
      if (headerFn) headerFn();
      let yl = y, yr = y;
      items.forEach((it, i) => {
        if (i < split) yl = draw(it, M, yl, colW);
        else yr = draw(it, M + colW + gap, yr, colW);
      });
      y = Math.max(yl, yr) + 1.5;
    };

    // ---- header: logo + red name left; PROPOSAL + meta right ----
    // 194×177px source — height must follow the real aspect ratio or the
    // horse prints squashed (18.3mm wide → 16.7mm tall).
    try { doc.addImage(A.LOGO_PNG, 'PNG', M, y - 5, 18.3, 16.7); } catch (_) {}
    const tx = M + 21;
    R(true); doc.setFontSize(14).setTextColor(RED[0], RED[1], RED[2]);
    doc.text('Stalwart Electric, Inc.', tx, y);
    doc.setFontSize(16).text('PROPOSAL', W - M, y, { align: 'right' });
    RL(); doc.setFontSize(FS).setTextColor(51, 51, 51);
    doc.text('4712 Solano Way, Fair Oaks, CA 95628 • 916-860-4198', tx, y + 5);
    doc.text('bryant@stalwart-electric.com • license #: 1109509', tx, y + 5 + LH);
    doc.setTextColor(SUB[0], SUB[1], SUB[2]);
    doc.text('Electrical Services – Labor Only', W - M, y + 5, { align: 'right' });
    let my = y + 11.5;
    [['Proposal #', proposalNumber],
     ['Issue Date', longDate(new Date())],
     ['Expiration Date', longDate(new Date(Date.now() + 30 * 24 * 3600 * 1000))],
    ].forEach(([k, v]) => {
      doc.setFontSize(FS);
      R(true); doc.setTextColor(INK[0], INK[1], INK[2]); doc.text(k, W - M - 33, my, { align: 'right' });
      R(false); doc.text(v, W - M, my, { align: 'right' });
      my += LH;
    });
    y = my + 2.5;

    // section head: bold uppercase + light descriptor, thick dark rule
    const h2 = (t, sub) => {
      // keep-with-next: never strand a header at the bottom of a page, and
      // give it the master's breathing room above (unless at a page top)
      nl(26);
      if (y > 15.5) y += 2.6;
      R(true); doc.setFontSize(FS + 0.3).setTextColor(INK[0], INK[1], INK[2]);
      const main = t.toUpperCase();
      doc.text(main, M, y);
      if (sub) {
        const tw = doc.getTextWidth(main + ' ');
        RL(); doc.setFontSize(FS).setTextColor(SUB[0], SUB[1], SUB[2]);
        doc.text(sub, M + tw + 0.5, y);
      }
      doc.setDrawColor(INK[0], INK[1], INK[2]); doc.setLineWidth(0.45);
      doc.line(M, y + 1.4, W - M, y + 1.4);
      y += 6.2;
    };
    const kvRow = (k, v, labelW = 27.5) => {
      if (!v) return;
      R(false); doc.setFontSize(FS);
      const lines = doc.splitTextToSize(v, W - M * 2 - labelW);
      nl(lines.length * LH + 1);
      R(true); doc.setTextColor(INK[0], INK[1], INK[2]); doc.text(k, M, y);
      R(false); doc.text(lines, M + labelW, y);
      y += lines.length * LH + 0.7;
    };

    h2('Project');
    kvRow('Prepared for', f.preparedFor);
    kvRow('Project', f.projectName);
    kvRow('Plans', f.plansRef);
    kvRow('Description', f.description);
    kvRow('Basis', f.basis);
    y += 1.5;

    // ---- pricing: light gray header row, thin rules, red total ----
    h2('Pricing', '– flat labor fee, labor only');
    doc.autoTable({
      startY: y, margin: { left: M, right: M }, theme: 'plain',
      head: [['Item', 'Description', 'Qty', 'Unit', 'Amount']],
      body: lineItems.filter(li => li.description.trim()).map((li, i) => [
        String(i + 1), li.description, String(li.qty),
        money(li.unitPrice), money((Number(li.qty) || 0) * (Number(li.unitPrice) || 0)),
      ]),
      styles: { font: 'Roboto', fontSize: FS, cellPadding: { top: 1.3, bottom: 1.3, left: 1.8, right: 1.8 }, textColor: INK },
      headStyles: { fillColor: [232, 232, 232], textColor: INK, fontStyle: 'normal' },
      columnStyles: { 0: { cellWidth: 11 }, 2: { cellWidth: 13, halign: 'right' }, 3: { cellWidth: 25, halign: 'right' }, 4: { cellWidth: 27, halign: 'right' } },
      didParseCell: (d) => { if (d.section === 'head' && d.column.index >= 2) d.cell.styles.halign = 'right'; },
      didDrawCell: (d) => {
        if (d.section === 'body' && d.column.index === 4) {
          doc.setDrawColor(RULE[0], RULE[1], RULE[2]); doc.setLineWidth(0.21);
          doc.line(M, d.cell.y + d.cell.height, W - M, d.cell.y + d.cell.height);
        }
      },
    });
    y = doc.lastAutoTable.finalY + 5;
    R(true); doc.setFontSize(FS + 0.3).setTextColor(RED[0], RED[1], RED[2]);
    doc.text(money(total), W - M - 1.8, y, { align: 'right' });
    doc.text('TOTAL LABOR*', W - M - 34, y, { align: 'right' });
    y += 5.5;
    const fnRaw = (f.laborFootnote || '').trim();
    const fnBold = /^labor only\.?/i.test(fnRaw);
    const fnSegs = fnBold
      ? [{ t: '*' }, { t: 'Labor only.', bold: true }, { t: fnRaw.replace(/^labor only\.?\s*/i, '') }]
      : [{ t: '*' }, { t: fnRaw }];
    if (fnRaw) {
      nl(richH(fnSegs, W - M * 2) + 2);
      y = richDraw(fnSegs, M, y, W - M * 2) + 0.5;
    }

    // ---- scope: italic code line, two-column bullets ----
    scopeSections.filter(sc => sc.title.trim() || sc.bulletsText.trim()).forEach(sc => {
      const parts = sc.title.split(/\s+[—–]\s+/);
      const main = parts.length >= 3 ? parts.slice(0, 2).join(' — ') : sc.title;
      const sub = parts.length >= 3 ? '– ' + parts.slice(2).join(' — ') : '';
      // header + italic preamble print inside twoCol's page-break decision,
      // so the title can't strand at a page bottom
      doc.setFont('helvetica', 'italic'); doc.setFontSize(FS - 0.4);
      const pl = sc.preamble ? doc.splitTextToSize(sc.preamble, W - M * 2) : [];
      const headH = 11 + (pl.length ? pl.length * (LH - 0.4) + 1.8 : 0);
      const headerFn = () => {
        h2(main, sub);
        if (pl.length) {
          doc.setFont('helvetica', 'italic'); doc.setFontSize(FS - 0.4); doc.setTextColor(INK[0], INK[1], INK[2]);
          doc.text(pl, M, y);
          y += pl.length * (LH - 0.4) + 1.8;
        }
      };
      const bullets = sc.bulletsText.split('\n').map(x => x.trim()).filter(Boolean);
      const mB = (b, w) => { R(false); doc.setFontSize(FS); return doc.splitTextToSize(b, w - 4.2).length * LH + 0.4; };
      const dB = (b, x, y0, w) => {
        R(false); doc.setFontSize(FS); doc.setTextColor(INK[0], INK[1], INK[2]);
        const ls = doc.splitTextToSize(b, w - 4.2);
        doc.text('•', x + 0.8, y0);
        doc.text(ls, x + 4.2, y0);
        return y0 + ls.length * LH + 0.4;
      };
      twoCol(bullets, mB, dB, headerFn, headH);
      y += 1.5;
    });

    // ---- RFI: gray box with red left bar, bold "RFI." run-in ----
    if (f.rfi.trim()) {
      // Master-style numbered run-in: "(1) Bold topic. … (2) …". Hand-typed
      // "(n)" markers are respected; otherwise each LINE of the RFI field
      // becomes a numbered question with its lead (up to the first period
      // or colon) in bold.
      const rfiText = f.rfi.trim();
      const segs = [{ t: 'RFI.', bold: true }];
      const rfiLines = rfiText.split('\n').map(x => x.trim()).filter(Boolean);
      if (/\(\d+\)/.test(rfiText)) {
        const re = /\((\d+)\)\s+([^.:]{1,80}[.:])/g;
        let last = 0, m;
        while ((m = re.exec(rfiText)) !== null) {
          if (m.index > last) segs.push({ t: rfiText.slice(last, m.index) });
          segs.push({ t: `(${m[1]})` }, { t: m[2], bold: true });
          last = m.index + m[0].length;
        }
        segs.push({ t: rfiText.slice(last) });
      } else if (rfiLines.length >= 2) {
        rfiLines.forEach((line, i) => {
          const m = /^([^.:]{1,80}[.:])\s*/.exec(line);
          segs.push({ t: `(${i + 1})` });
          if (m) segs.push({ t: m[1], bold: true }, { t: line.slice(m[0].length) });
          else segs.push({ t: line });
        });
      } else {
        segs.push({ t: rfiText });
      }
      const padX = 2.5, padY = 2.2, barW = 1.05;
      const boxW = W - M * 2, txtW = boxW - padX * 2 - barW;
      const txtH = richH(segs, txtW, LH - 0.5);
      nl(txtH + padY * 2 + 4);
      const top = y - 1;
      doc.setFillColor(245, 245, 245); doc.rect(M, top, boxW, txtH + padY * 2, 'F');
      doc.setFillColor(RED[0], RED[1], RED[2]); doc.rect(M, top, barW, txtH + padY * 2, 'F');
      richDraw(segs, M + barW + padX, top + padY + 2.8, txtW, LH - 0.5);
      y = top + txtH + padY * 2 + 3.5;
    }

    // ---- exclusions: two-column numbered, bold lead phrases ----
    const exList = exclusions.split('\n').map(x => x.trim()).filter(Boolean);
    if (exList.length) {
      const items = exList.map((ex, i) => {
        const { lead, rest } = leadSplit(ex);
        return lead ? [{ t: (i + 1) + '.' }, { t: lead, bold: true }, { t: rest }] : [{ t: (i + 1) + '. ' + ex }];
      });
      const mE = (segs, w) => richH(segs, w, LH - 0.5, FS, 3.2) + 0.5;
      const dE = (segs, x, y0, w) => richDraw(segs, x, y0, w, LH - 0.5, FS, 3.2) + 0.5;
      twoCol(items, mE, dE, () => h2('Exclusions and Clarifications'), 11);
      y += 1.5;
    }

    // ---- terms: bold label column + ** payment footnote ----
    if (terms.some(t => t.label.trim())) {
      h2('Terms');
      terms.filter(t => t.label.trim() || t.text.trim()).forEach(t => kvRow(t.label, t.text, 34));
      if (f.paymentNote.trim()) {
        const pRaw = f.paymentNote.trim();
        const pBold = /^payment:?\s*/i.test(pRaw);
        const pSegs = pBold
          ? [{ t: '**' }, { t: 'Payment:', bold: true }, { t: pRaw.replace(/^payment:?\s*/i, '') }]
          : [{ t: '**' }, { t: pRaw }];
        nl(richH(pSegs, W - M * 2) + 3);
        y += 1;
        y = richDraw(pSegs, M, y, W - M * 2);
      }
    }

    // ---- exhibit list + signature lines (kept together on one page) ----
    const exListSegs = exhibits.length ? [
      { t: 'Exhibits (attached and part of this proposal):', bold: true },
      { t: exhibits.map((ex, i) => {
          const title = (ex.title || '').trim() || ex.name.replace(/\.[^.]+$/, '');
          const pg = ex.pages > 0 ? ` (${ex.pages} page${ex.pages === 1 ? '' : 's'})` : '';
          return `${i + 1} – ${title}${pg}`;
        }).join('; ') },
    ] : null;
    const exListH = exListSegs ? richH(exListSegs, W - M * 2) + 4 : 0;
    nl(34 + exListH);
    if (exListSegs) {
      y += 3;
      y = richDraw(exListSegs, M, y, W - M * 2);
    }
    y += 10;
    doc.setDrawColor(INK[0], INK[1], INK[2]); doc.setLineWidth(0.35);
    const half = (W - M * 2 - 9) / 2;
    doc.line(M, y, M + half, y); doc.line(W - M - half, y, W - M, y);
    y += 4.4;
    R(true); doc.setFontSize(FS); doc.setTextColor(INK[0], INK[1], INK[2]);
    doc.text('Accepted by ' + (f.preparedFor || 'Client') + ' / Date', M, y);
    doc.text('Stalwart Electric, Inc. / Date', W - M - half, y);
    y += 8;
    nl(12);
    RL(); doc.setFontSize(FS - 0.2); doc.setTextColor(51, 51, 51);
    const legal = doc.splitTextToSize('Contractors are required by law to be licensed and regulated by the Contractors State License Board, which has jurisdiction to investigate complaints against contractors. Stalwart Electric, Inc. – CSLB #1109509.', W - M * 2);
    doc.text(legal, M, y);

    // footer: PAGE X OF Y — 8pt bold, right-aligned, like the master.
    // Skipped when exhibits are attached: the merge step numbers the whole
    // package (proposal + exhibit pages) as one sequence instead.
    if (!skipFooter) {
      const pages = doc.getNumberOfPages();
      for (let i = 1; i <= pages; i++) {
        doc.setPage(i); R(true); doc.setFontSize(8); doc.setTextColor(85, 85, 85);
        doc.text('PAGE ' + i + ' OF ' + pages, W - 15.2, 272, { align: 'right' });
      }
    }
    return doc;
  };

  // ---- Exhibit bundling (pdf-lib, loaded on demand) ----
  // Appends every exhibit to the proposal as 8.5×11 PORTRAIT pages: PDF pages
  // keep vector quality (blueprints stay sharp) scaled to fit, images are
  // centered, and each page gets an "EXHIBIT n — PAGE x OF y" stamp.
  const mergeExhibits = async (baseBytes) => {
    const { PDFDocument, StandardFonts, rgb, degrees } = await import('pdf-lib');
    const LW = 612, LH = 792; // letter, in points
    const out = await PDFDocument.load(baseBytes);
    const font = await out.embedFont(StandardFonts.HelveticaBold);
    const stamp = (page, label) => {
      const tw = font.widthOfTextAtSize(label, 9);
      page.drawRectangle({ x: LW - tw - 34, y: LH - 28, width: tw + 16, height: 16, color: rgb(1, 1, 1), opacity: 0.88, borderColor: rgb(0.25, 0.25, 0.25), borderWidth: 0.7 });
      page.drawText(label, { x: LW - tw - 26, y: LH - 23.5, size: 9, font, color: rgb(0.1, 0.1, 0.1) });
    };
    for (let e = 0; e < exhibits.length; e++) {
      const ex = exhibits[e];
      const { data } = await axios.get(`${API_BASE}/proposals/exhibits/file?path=${encodeURIComponent(ex.path)}`, { ...authH(), responseType: 'arraybuffer' });
      // Landscape content (blueprints, wide photos) rotates 90° to FILL the
      // portrait page instead of shrinking into its top half — roughly
      // doubles the drawing size. Portrait content stays upright.
      const place = (page, ow, oh, drawFn) => {
        const landscape = ow > oh * 1.05;
        if (landscape) {
          const s = Math.min((LW - 36) / oh, (LH - 56) / ow);
          const w = ow * s, h = oh * s; // w runs vertically after rotation
          drawFn({ x: (LW - h) / 2 + h, y: (LH - 36 - w) / 2, width: w, height: h, rotate: degrees(90) });
        } else {
          const s = Math.min((LW - 36) / ow, (LH - 56) / oh);
          const w = ow * s, h = oh * s;
          drawFn({ x: (LW - w) / 2, y: (LH - 36 - h) / 2, width: w, height: h });
        }
      };
      if (/\.pdf$/i.test(ex.name)) {
        const src = await PDFDocument.load(data, { ignoreEncryption: true });
        const embedded = await out.embedPdf(src, src.getPageIndices());
        embedded.forEach((ep, i) => {
          const page = out.addPage([LW, LH]);
          place(page, ep.width, ep.height, (opts) => page.drawPage(ep, opts));
          stamp(page, `EXHIBIT ${e + 1} — PAGE ${i + 1} OF ${embedded.length}`);
        });
      } else {
        const img = /\.png$/i.test(ex.name) ? await out.embedPng(data) : await out.embedJpg(data);
        const page = out.addPage([LW, LH]);
        place(page, img.width, img.height, (opts) => page.drawImage(img, opts));
        stamp(page, `EXHIBIT ${e + 1} — PAGE 1 OF 1`);
      }
    }
    // Package page numbers: ONE sequence across proposal + exhibit pages,
    // same footer style as the proposal (8pt bold, bottom right). The small
    // white backing keeps it readable over exhibit drawing content.
    const all = out.getPages();
    const N = all.length;
    all.forEach((pg, i) => {
      const label = `PAGE ${i + 1} OF ${N}`;
      const tw = font.widthOfTextAtSize(label, 8);
      pg.drawRectangle({ x: LW - 43 - tw - 4, y: 16.5, width: tw + 8, height: 11, color: rgb(1, 1, 1), opacity: 0.85 });
      pg.drawText(label, { x: LW - 43 - tw, y: 20, size: 8, font, color: rgb(0.33, 0.33, 0.33) });
    });
    return out.save();
  };

  const bundleBytes = async () => {
    // With exhibits, the proposal prints footer-less and the merge numbers
    // the whole package 1..N; without, the proposal numbers itself.
    const doc = await buildPDF(exhibits.length > 0);
    if (!exhibits.length) return doc.output('arraybuffer');
    return mergeExhibits(doc.output('arraybuffer'));
  };

  const download = async () => {
    const fname = `Proposal_${proposalNumber.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
    try {
      const bytes = await bundleBytes();
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob); a.download = fname; a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 10000);
    } catch (e) { alert('Could not build the PDF bundle: ' + (e.response?.data?.error || e.message)); }
  };

  const send = async () => {
    if (sendStage) return;
    setSendStage('saving');
    try {
      const saved = await save();
      if (!saved) return;
      setSendStage('pdf');
      await new Promise(r => setTimeout(r, 30));
      // Same bundle as Download — exhibits ride along in the emailed PDF too.
      const bytes = await bundleBytes();
      let bin = ''; const u8 = new Uint8Array(bytes);
      for (let i = 0; i < u8.length; i += 0x8000) bin += String.fromCharCode.apply(null, u8.subarray(i, i + 0x8000));
      const pdfBase64 = 'data:application/pdf;base64,' + btoa(bin);
      setSendStage('draft');
      // Professional cover note built from the proposal itself — recipients
      // are GCs and owners, so it reads like business correspondence.
      const gcName = f.preparedFor.replace(/\s*\([^)]*\)\s*$/, '').trim();
      const subject = `Labor Proposal ${proposalNumber} — ${f.projectName.trim() || 'Stalwart Electric, Inc.'}`;
      const mailBody =
        `Dear ${gcName || 'Sir or Madam'},\n\n` +
        `Please find attached Labor Proposal ${proposalNumber} for ${f.projectName.trim() || 'the referenced project'}, in the amount of ${money(total)}.\n\n` +
        `The proposal sets out the full scope of work, exclusions and clarifications, and our terms${exhibits.length ? ', together with the referenced exhibits' : ''}. Pricing is valid for 30 days from the issue date.\n\n` +
        `We appreciate the opportunity to be considered for this project. Should you have any questions, or wish to review any portion of the scope, I would be glad to discuss at your convenience.\n\n` +
        `Respectfully,\n\n` +
        `Bryant\n` +
        `Stalwart Electric, Inc.\n` +
        `CSLB #1109509 · C-10 Electrical\n` +
        `916-860-4198 · bryant@stalwart-electric.com`;
      const fname = `Proposal_${proposalNumber.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
      try {
        await axios.post(`${API_BASE}/invoices/create-outlook-draft`, {
          to: '', subject, body: mailBody, pdfBase64, filename: fname,
        }, authH());
        setToastMsg('Draft with PDF created in your Outlook Drafts — review & send');
      } catch (err) {
        // Attachment too large (or rejected) — still create the draft, bare,
        // and download the PDF locally so it can be attached by hand.
        await axios.post(`${API_BASE}/invoices/create-outlook-draft`, {
          to: '', subject,
          body: mailBody.replace(/^Dear /, '[ATTACH THE PROPOSAL PDF BEFORE SENDING — it was too large to attach automatically. Delete this line.]\n\nDear '),
        }, authH());
        const blob = new Blob([bytes], { type: 'application/pdf' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob); a.download = fname; a.click();
        setTimeout(() => URL.revokeObjectURL(a.href), 10000);
        setToastMsg('PDF was too large to attach — draft created without it, and the PDF downloaded so you can attach it in Outlook.');
      }
      if (saved._id) axios.put(`${API_BASE}/proposals/${saved._id}`, { status: 'Sent' }, authH()).then(() => setStatus('Sent')).catch(() => {});
    } catch (e) {
      alert('Could not create the Outlook draft: ' + (e.response?.data?.error || e.message));
    } finally { setSendStage(''); }
  };

  const input = "w-full px-3.5 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-sm text-zinc-200 placeholder-zinc-500 focus:outline-none focus:border-indigo-600/60";
  const lbl = "block text-[11px] font-bold uppercase tracking-wide text-zinc-500 mb-1";
  const card = "bg-gray-900 rounded-2xl border border-gray-800/50 p-6 max-md:p-4 shadow-xl";

  return (
    <div className="min-h-screen bg-gray-950 text-zinc-100 p-8 max-md:p-3 space-y-5 max-w-4xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl max-md:text-xl font-bold tracking-tight">Labor Proposal <span className="text-zinc-500 font-mono text-base">{proposalNumber}</span></h1>
          {bidTitle && <button onClick={() => navigate(`/contacts?bid=${bidRef}&section=quotes`)} className="text-xs text-indigo-400 hover:text-indigo-300">← Back to bid "{bidTitle}"</button>}
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-[10px] font-bold uppercase px-2.5 py-1 rounded-full ${status === 'Accepted' ? 'bg-emerald-500/15 text-emerald-300' : status === 'Sent' ? 'bg-sky-500/15 text-sky-300' : 'bg-zinc-700/40 text-zinc-300'}`}>{status}</span>
          <button onClick={save} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-sm font-semibold text-white">Save</button>
          <button onClick={download} className="px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg text-sm font-semibold text-white">Download</button>
          <button onClick={send} disabled={!!sendStage} className="px-4 py-2 bg-red-600 hover:bg-red-500 disabled:opacity-70 rounded-lg text-sm font-semibold text-white" style={{ minWidth: 72 }}>
            {sendStage === 'saving' ? 'Saving…' : sendStage === 'pdf' ? 'Building PDF…' : sendStage === 'draft' ? 'Creating draft…' : 'Send'}
          </button>
        </div>
      </div>

      {toast && <p className="text-sm text-emerald-400 font-semibold">{toast}</p>}

      <div className={card}>
        <h2 className="text-lg font-bold mb-4">Project</h2>
        <div className="space-y-3">
          <div className="relative">
            <label className={lbl}>Prepared for (GC)</label>
            <input className={input} placeholder="Type to search customers & companies — or enter a new name"
              value={f.preparedFor}
              onChange={e => { set('preparedFor', e.target.value); setPfSel(null); setPfOpen(true); }}
              onFocus={() => setPfOpen(true)}
              onBlur={() => setTimeout(() => setPfOpen(false), 180)} />
            {pfOpen && pfQuery.length >= 2 && !pfSel && (
              <div className="absolute z-40 left-0 right-0 mt-1 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl overflow-hidden">
                {pfMatches.map((o, i) => (
                  <button key={i} onMouseDown={(e) => { e.preventDefault(); pickPf(o); }} className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-800 text-left">
                    <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded shrink-0 ${o.kind === 'company' ? 'bg-violet-500/15 text-violet-300' : 'bg-sky-500/15 text-sky-300'}`}>{o.kind === 'company' ? 'Company' : 'Customer'}</span>
                    <span className="text-sm text-zinc-200 truncate">{o.name}</span>
                    {o.phone && <span className="text-[11px] text-zinc-500 ml-auto shrink-0">{o.phone}</span>}
                  </button>
                ))}
                <button onMouseDown={(e) => { e.preventDefault(); addPfCustomer(); }} className={`w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-800 text-left ${pfMatches.length ? 'border-t border-gray-800' : ''}`}>
                  <Plus className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                  <span className="text-sm text-emerald-300 truncate">Add "{f.preparedFor.replace(/\s*\(General Contractor\)\s*$/i, '').trim()}" as a new customer</span>
                </button>
              </div>
            )}
          </div>
          <div><label className={lbl}>Project *</label><input className={input} placeholder="e.g. Patel 6-Unit ADUs — 593 S. 6th St, San Jose, CA" value={f.projectName} onChange={e => set('projectName', e.target.value)} /></div>
          <div><label className={lbl}>Plans</label><input className={input} placeholder="e.g. Full Plan Set dated 3/8/2026 — sheets A1.1, E-1.0…" value={f.plansRef} onChange={e => set('plansRef', e.target.value)} /></div>
          <div><label className={lbl}>Description</label><textarea rows={2} className={input + ' resize-y'} value={f.description} onChange={e => set('description', e.target.value)} /></div>
          <div><label className={lbl}>Basis</label><textarea rows={2} className={input + ' resize-y'} value={f.basis} onChange={e => set('basis', e.target.value)} /></div>
        </div>
      </div>

      <div className={card}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold">Pricing — labor only</h2>
          <span className="text-emerald-300 font-bold">{money(total)}</span>
        </div>
        {lineItems.map((li, i) => (
          <div key={i} className="grid grid-cols-[1fr_70px_110px_30px] max-md:grid-cols-[1fr_60px_90px_26px] gap-2 mb-2 items-center">
            <input className={input} placeholder="Description" value={li.description} onChange={e => setLineItems(ls => ls.map((x, j) => j === i ? { ...x, description: e.target.value } : x))} />
            <input className={input} inputMode="decimal" placeholder="Qty" value={li.qty} onChange={e => setLineItems(ls => ls.map((x, j) => j === i ? { ...x, qty: e.target.value } : x))} />
            <input className={input} inputMode="decimal" placeholder="Unit $" value={li.unitPrice} onChange={e => setLineItems(ls => ls.map((x, j) => j === i ? { ...x, unitPrice: e.target.value } : x))} />
            <button onClick={() => setLineItems(ls => ls.length > 1 ? ls.filter((_, j) => j !== i) : ls)} className="text-zinc-600 hover:text-red-400"><X className="w-4 h-4" /></button>
          </div>
        ))}
        <button onClick={() => setLineItems(ls => [...ls, { description: '', qty: 1, unitPrice: 0 }])} className="text-xs text-indigo-400 hover:text-indigo-300 flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Add line item</button>
        <div className="mt-3"><label className={lbl}>Labor-only footnote</label><textarea rows={2} className={input + ' resize-y text-xs'} value={f.laborFootnote} onChange={e => set('laborFootnote', e.target.value)} /></div>

        {/* $/sqft analytics */}
        <div className="mt-4 p-3 bg-gray-950/60 border border-gray-800/70 rounded-xl">
          <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-2">Square footage (for $/sqft tracking — optional)</p>
          <div className="grid grid-cols-3 max-md:grid-cols-1 gap-2">
            <div><label className={lbl}>Units</label><input className={input} inputMode="numeric" placeholder="e.g. 6" value={f.units} onChange={e => set('units', e.target.value)} /></div>
            <div><label className={lbl}>Sqft / unit</label><input className={input} inputMode="decimal" placeholder="e.g. 442.5" value={f.sqftPerUnit} onChange={e => set('sqftPerUnit', e.target.value)} /></div>
            <div><label className={lbl}>Total sqft</label><input className={input} inputMode="decimal" placeholder="auto" value={f.totalSqft || (effSqft ? String(effSqft) : '')} onChange={e => set('totalSqft', e.target.value)} /></div>
          </div>
          {perSqft != null && (
            <p className="text-sm mt-2 text-zinc-300">≈ <span className="text-indigo-300 font-bold">{money(perSqft)}/sqft</span> labor across {effSqft.toLocaleString()} sqft</p>
          )}
        </div>
      </div>

      <div className={card}>
        <div className="flex items-center justify-between mb-4"><h2 className="text-lg font-bold">Scope</h2>
          <button onClick={() => setScopeSections(ss => [...ss, { title: `SCOPE — ITEM ${ss.length + 1}`, preamble: DEFAULT_PREAMBLE, bulletsText: '' }])} className="text-xs text-indigo-400 hover:text-indigo-300 flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Add section</button>
        </div>
        {scopeSections.map((sc, i) => (
          <div key={i} className="mb-4 p-3 bg-gray-950/50 border border-gray-800/60 rounded-xl space-y-2">
            <div className="flex gap-2">
              <input className={input} value={sc.title} onChange={e => setScopeSections(ss => ss.map((x, j) => j === i ? { ...x, title: e.target.value } : x))} />
              <button onClick={() => setScopeSections(ss => ss.filter((_, j) => j !== i))} className="text-zinc-600 hover:text-red-400 shrink-0"><Trash2 className="w-4 h-4" /></button>
            </div>
            <input className={input + ' text-xs'} placeholder="Code-compliance preamble" value={sc.preamble} onChange={e => setScopeSections(ss => ss.map((x, j) => j === i ? { ...x, preamble: e.target.value } : x))} />
            <textarea rows={6} className={input + ' resize-y text-xs'} placeholder={'One scope bullet per line, e.g.\n100A 120/240V sub panel set, terminated, labeled\nKitchen: two 20A small-appliance circuits…'} value={sc.bulletsText} onChange={e => setScopeSections(ss => ss.map((x, j) => j === i ? { ...x, bulletsText: e.target.value } : x))} />
          </div>
        ))}
      </div>

      <div className={card}>
        <h2 className="text-lg font-bold mb-2">RFI <span className="text-zinc-600 text-xs font-normal">(optional — only prints when filled)</span></h2>
        <textarea rows={4} className={input + ' resize-y text-xs'} placeholder="Open questions for the GC/architect…" value={f.rfi} onChange={e => set('rfi', e.target.value)} />
      </div>

      <div className={card}>
        <h2 className="text-lg font-bold mb-2">Exclusions &amp; Clarifications <span className="text-zinc-600 text-xs font-normal">(one per line — numbered automatically)</span></h2>
        <textarea rows={10} className={input + ' resize-y text-xs'} value={exclusions} onChange={e => setExclusions(e.target.value)} />
      </div>

      <div className={card}>
        <div className="flex items-center justify-between mb-4"><h2 className="text-lg font-bold">Terms</h2>
          <button onClick={() => setTerms(ts => [...ts, { label: '', text: '' }])} className="text-xs text-indigo-400 hover:text-indigo-300 flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Add term</button>
        </div>
        {terms.map((t, i) => (
          <div key={i} className="grid grid-cols-[140px_1fr_30px] max-md:grid-cols-1 gap-2 mb-2">
            <input className={input + ' text-xs font-semibold'} placeholder="Label" value={t.label} onChange={e => setTerms(ts => ts.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} />
            <textarea rows={2} className={input + ' resize-y text-xs'} value={t.text} onChange={e => setTerms(ts => ts.map((x, j) => j === i ? { ...x, text: e.target.value } : x))} />
            <button onClick={() => setTerms(ts => ts.filter((_, j) => j !== i))} className="text-zinc-600 hover:text-red-400 max-md:hidden"><X className="w-4 h-4" /></button>
          </div>
        ))}
        <div className="mt-2"><label className={lbl}>Payment footnote (optional, prints as **)</label><textarea rows={2} className={input + ' resize-y text-xs'} value={f.paymentNote} onChange={e => set('paymentNote', e.target.value)} /></div>
      </div>

      {/* ---- Exhibits: bundled into the downloaded/emailed PDF ---- */}
      <div
        className={card + ' relative'}
        onDragOver={(e) => { if (e.dataTransfer?.types?.includes('Files')) { e.preventDefault(); setExDragging(true); } }}
        onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setExDragging(false); }}
        onDrop={(e) => { if (!e.dataTransfer?.files?.length) return; e.preventDefault(); setExDragging(false); addExhibits(e.dataTransfer.files); }}
      >
        {exDragging && <div className="absolute inset-0 z-30 rounded-2xl border-2 border-dashed border-indigo-500 bg-indigo-500/10 pointer-events-none" />}
        <h2 className="text-lg font-bold mb-1">Exhibits <span className="text-zinc-600 text-xs font-normal">(bundled into the PDF — all pages 8.5 × 11 portrait)</span></h2>
        <p className="text-xs text-zinc-500 mb-3">Drag &amp; drop PDFs or photos — each becomes Exhibit 1, 2, 3… in order, stamped "EXHIBIT n — PAGE x OF y". Arrows renumber. Export Word docs to PDF first.</p>
        {exBusy && <p className="text-[11px] font-black uppercase tracking-widest text-indigo-300 animate-pulse mb-2">Uploading… {exBusy}</p>}
        {exhibits.map((ex, i) => (
          <div key={ex.path + i} className="flex items-center gap-2.5 bg-gray-950/60 border border-gray-800/70 rounded-xl px-3.5 py-2.5 mb-1.5">
            <span className="text-[10px] font-black uppercase tracking-wide px-2 py-1 rounded-lg bg-indigo-500/15 text-indigo-300 shrink-0">Exhibit {i + 1}</span>
            <input
              className="flex-1 min-w-0 bg-gray-900 border border-gray-800 rounded-lg px-2.5 py-1.5 text-sm text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-indigo-600/60"
              placeholder={ex.name.replace(/\.[^.]+$/, '')}
              value={ex.title || ''}
              title={ex.name}
              onChange={(ev) => setExhibits(prev => prev.map((x, j) => j === i ? { ...x, title: ev.target.value } : x))}
            />
            <span className="text-[10px] text-zinc-600 shrink-0 max-md:hidden">{ex.size ? `${Math.round(ex.size / 1024)} KB` : ''}</span>
            <button onClick={() => moveExhibit(i, -1)} disabled={i === 0} className="p-1 rounded text-zinc-500 hover:text-zinc-200 disabled:opacity-30" title="Move up"><ChevronUp className="w-4 h-4" /></button>
            <button onClick={() => moveExhibit(i, 1)} disabled={i === exhibits.length - 1} className="p-1 rounded text-zinc-500 hover:text-zinc-200 disabled:opacity-30" title="Move down"><ChevronDown className="w-4 h-4" /></button>
            <button onClick={() => removeExhibit(i)} className="p-1 rounded text-zinc-500 hover:text-red-400" title="Remove"><X className="w-4 h-4" /></button>
          </div>
        ))}
        {exhibits.length === 0 && !exBusy ? (
          <label className="block border-2 border-dashed border-gray-700 hover:border-gray-600 rounded-xl py-8 text-center text-xs text-zinc-500 cursor-pointer">
            Drop files here, or click to browse
            <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={(e) => { addExhibits(e.target.files); e.target.value = ''; }} />
          </label>
        ) : (
          <label className="inline-flex items-center gap-1 mt-1 text-xs text-indigo-400 hover:text-indigo-300 cursor-pointer">
            <Plus className="w-3.5 h-3.5" /> Add exhibit
            <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={(e) => { addExhibits(e.target.files); e.target.value = ''; }} />
          </label>
        )}
      </div>
    </div>
  );
}

export default CreateProposal;
